/**
 * PinSetupScreen — Sprint 1.5b.2
 *
 * In questo flusso il PIN si imposta PRIMA del claim sigil:
 *   - Cifra mnemonica con PIN + sigilLower (ma il sigil non c'e' ancora!)
 *   ↓ allora cosa fa il PIN setup senza sigil?
 *
 * Soluzione: il PIN derivante depende anche dal sigil, ma a questo punto
 * non lo abbiamo ancora. Quindi:
 *   - Qui scegli solo il PIN e lo memorizziamo in memoria (store React)
 *   - Verifichiamo che il PIN sia 6 cifre e robusto
 *   - Nessun upload, nessun localStorage finche' non c'e' anche il sigil
 *   - Avanti a /sigil che fa l'atomic claim+backup
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import OnboardSteps from '../components/OnboardSteps';
import { useStore } from '../lib/store';
import { isValidPin, isPinStrong } from '../lib/pinCrypto';

export default function PinSetupScreen() {
  const navigate = useNavigate();
  const mnemonic = useStore((s) => s.mnemonic);
  const setPendingPin = useStore((s) => s.setPendingPin);

  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [err, setErr] = useState(null);

  function onDigit(d) {
    // Updater FUNZIONALE: tap ravvicinati si accodano invece di sovrascriversi
    // (niente cifre "saltate"). La scelta del campo usa la lunghezza corrente.
    if (pin.length < 6) setPin((prev) => (prev.length < 6 ? prev + d : prev));
    else setConfirmPin((prev) => (prev.length < 6 ? prev + d : prev));
  }
  function onBack() {
    if (confirmPin.length > 0) setConfirmPin((prev) => prev.slice(0, -1));
    else setPin((prev) => prev.slice(0, -1));
  }

  function onConfirm() {
    setErr(null);
    if (!isValidPin(pin)) { setErr('Il PIN deve avere 6 cifre'); return; }
    if (!isPinStrong(pin)) {
      setErr('PIN troppo facile. Evita 123456 o 111111.');
      return;
    }
    if (pin !== confirmPin) { setErr('I due PIN non coincidono'); return; }
    if (!mnemonic) {
      setErr('Sessione persa. Ricomincia da capo.');
      return;
    }

    // Memorizza il PIN in memoria temporanea per il claim atomico.
    // Verra' usato in SigilScreen per cifrare la mnemonica al momento
    // del claim, e poi cancellato.
    setPendingPin(pin);
    navigate('/sigil', { replace: true });
  }

  const dots = (val, len = 6) =>
    Array.from({ length: len })
      .map((_, i) => (i < val.length ? '●' : '○'))
      .join(' ');

  const showingConfirm = pin.length === 6;

  if (!mnemonic) {
    return (
      <Frame statusLeft="PIN_SETUP :: ERROR" statusRight="5G">
        <Header title="// IMPOSTA PIN" />
        <Box variant="danger" className="text-[16px] space-y-2">
          <p><span className="text-red-500">✕</span> sessione persa.</p>
          <p className="text-ink-dim">
            Forse hai ricaricato la pagina. Ricomincia da capo.
          </p>
        </Box>
        <Footer>
          <Btn variant="ghost" onClick={() => navigate('/')}>◀ ricomincia</Btn>
        </Footer>
      </Frame>
    );
  }

  return (
    <Frame statusLeft="PIN_SETUP" statusRight="5G">
      <Header title="// IMPOSTA PIN" />
      <OnboardSteps step={3} />

      <p className="text-[14px] text-ink-dim mb-1.5">
        Scegli un PIN di 6 cifre per riaprire Nullum. Le 24 parole restano la tua scorta.
      </p>

      <div className="mb-2">
        <div className="text-[12px] text-ink-dim mb-1">
          {showingConfirm ? '> ripeti PIN' : '> PIN'}
        </div>
        <div className="text-ink text-glow text-[24px] tracking-[0.3em] whitespace-nowrap font-mono py-2.5 px-3 border border-ink-dim/50 rounded-xl bg-ink/[0.02] text-center">
          {showingConfirm ? dots(confirmPin) : dots(pin)}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-1.5 mt-1.5">
        {[1,2,3,4,5,6,7,8,9].map((n) => (
          <button key={n} onPointerDown={() => onDigit(String(n))}
            className="rounded-xl border border-ink-dim/50 bg-ink/[0.02] text-ink py-2.5 text-[22px] font-mono hover:bg-ink/[0.08] active:bg-ink/[0.12] transition-colors touch-manipulation select-none">
            {n}
          </button>
        ))}
        <button onPointerDown={onBack}
          className="rounded-xl border border-ink-dim/50 bg-ink/[0.02] text-amber py-2.5 text-[18px] font-mono hover:bg-amber/10 active:bg-amber/20 transition-colors touch-manipulation select-none">◀</button>
        <button onPointerDown={() => onDigit('0')}
          className="rounded-xl border border-ink-dim/50 bg-ink/[0.02] text-ink py-2.5 text-[22px] font-mono hover:bg-ink/[0.08] active:bg-ink/[0.12] transition-colors touch-manipulation select-none">0</button>
        <button onClick={() => { setPin(''); setConfirmPin(''); }}
          className="rounded-xl border border-ink-dim/50 bg-ink/[0.02] text-amber py-2.5 text-[14px] font-mono hover:bg-amber/10 active:bg-amber/20 transition-colors touch-manipulation select-none">CLR</button>
      </div>

      {err && (
        <Box variant="danger" className="text-[16px] mt-3">
          <span className="text-red-500">✕</span> {err}
        </Box>
      )}

      <Footer>
        <Btn variant="primary" onClick={onConfirm}
             disabled={pin.length !== 6 || confirmPin.length !== 6}>
          Avanti — scegli il nome ▶
        </Btn>
      </Footer>
    </Frame>
  );
}
